import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import { WhpClass, WhpClassFraction, WhpStats } from '../lib/types';
import { WHP_PALETTE } from '../lib/whpColors';

// CSV schema: class,label,fraction_of_shed,acres
// One row per WHP class plus one `non_burnable` row covering water, developed,
// and nodata pixels. `fraction_of_shed` is the share of the full supply shed;
// the UI derives class-of-burnable fractions from the per-class acres.
const VALID_CLASSES = new Set<WhpClass>(WHP_PALETTE.order);

const parseWhpStats = (text: string): WhpStats => {
  const rows = parseCsv(text);
  const classAcres = new Map<WhpClass, number>();
  let nonBurnableAcres = 0;
  let totalAcres = 0;

  rows.forEach(row => {
    const acres = Number(row['acres']);
    if (!Number.isFinite(acres)) return;
    totalAcres += acres;

    const cls = row['class'];
    if (cls === 'non_burnable') {
      nonBurnableAcres = acres;
    } else if (VALID_CLASSES.has(cls as WhpClass)) {
      classAcres.set(cls as WhpClass, acres);
    }
  });

  const burnableAcres = Math.max(totalAcres - nonBurnableAcres, 0);
  const classes: WhpClassFraction[] = WHP_PALETTE.order.map(cls => {
    const acres = classAcres.get(cls) ?? 0;
    return {
      class: cls,
      burnableAcres: acres,
      fraction: burnableAcres > 0 ? acres / burnableAcres : 0,
    };
  });

  return {
    classes,
    nonBurnableFraction: totalAcres > 0 ? nonBurnableAcres / totalAcres : 0,
    totalAcres,
  };
};

/**
 * Fetches WHP (Wildfire Hazard Potential) class-fraction stats for the supply
 * shed defined by (millCode, driveTime). CSV lives at
 * sfi/whp/{mill}_{driveTime}min_whp.csv.
 */
export const useWildfireHazardPotential = createCsvStatsHook({
  path: API_CONFIG.s3.paths.whpStats,
  parse: parseWhpStats,
  errorPrefix: 'Failed to load wildfire hazard data',
});
